import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ZoomEnPestana } from '@/components/agenda/ZoomHoja';
import { AsuntoEnPestana } from '@/components/athena/HojaDelAsunto';
import { ClienteEnPestana } from '@/components/cartera/VentanaDeFicha';
import { ChatEnPestana } from '@/pages/chat/ChatDeAthena';
import { useMenu } from '@/hooks/useMenu';
import { cn } from '@/lib/cn';
import { PANEL_DE_PESTANAS, PANTALLA_DE_FONDO, idDePestana, nombreDeLaPantalla, vuelveALaPantalla, type Pestana } from '@/lib/pestanas';
import { usePestanas } from '@/store/pestanas.store';
import { BarraDePestanas } from './BarraDePestanas';

/**
 * EL ANFITRIÓN DE PESTAÑAS (tanda 6, T12). Va en `Layout`, entre la cabecera y el contenido:
 * pinta la barra y, si hay una pestaña de detalle activa, su contenido en el sitio de la
 * pantalla. Sin velo ni fondo borroso.
 *
 * ═══ LA PANTALLA DE ATRÁS NO SE DESMONTA ═══
 *
 * Se esconde con `visibility: hidden`, no con `display: none`: así conserva su tamaño y, con
 * él, dónde iba el desplazamiento de cada lista. Al volver a su pestaña está como se dejó.
 *
 * De las pestañas de detalle solo está montada la activa: la ficha «se desmonta al cerrar
 * para no arrastrar una edición a medias», y doce fichas vivas serían doce juegos de consultas.
 */
export function AnfitrionDePestanas({ children }: { children: ReactNode }) {
  const abiertas = usePestanas((s) => s.abiertas);
  const activa = usePestanas((s) => s.activa);
  const activarPestana = usePestanas((s) => s.activarPestana);
  const cerrarPestana = usePestanas((s) => s.cerrarPestana);
  const lugar = useLocation();
  const pantalla = nombreDeLaPantalla(useMenu().datos?.rutas, lugar.pathname);
  const laActiva = abiertas.find((p) => p.clave === activa);

  // Navegar (el menú, «Ver mensaje», «Abrir el chat», atrás) vuelve a la pantalla; las de
  // detalle siguen abiertas en la barra.
  const lugarDeAntes = useRef(lugar.key);
  useEffect(() => {
    if (lugarDeAntes.current === lugar.key) return;
    lugarDeAntes.current = lugar.key;
    if (vuelveALaPantalla(lugar)) activarPestana(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `lugar.key` cambia con cada navegación; el resto de `lugar` va con él.
  }, [lugar.key, activarPestana]);

  // Esc vuelve a la pantalla (no cierra la pestaña). En la fase de captura, antes que nadie:
  // si hay un diálogo encima, Esc es para él; dentro de un campo de texto, para dejar de editar.
  const hayActiva = Boolean(laActiva);
  useEffect(() => {
    if (!hayActiva) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const donde = e.target instanceof Element ? e.target : null;
      if (donde?.closest('input, textarea, [contenteditable="true"]') || document.querySelector('[role="dialog"]')) return;
      activarPestana(null);
    };
    window.addEventListener('keydown', alTeclear, true);
    return () => window.removeEventListener('keydown', alTeclear, true);
  }, [hayActiva, activarPestana]);

  // El foco acompaña: al abrir una pestaña pasa a su contenido (un lector de pantalla lo anuncia
  // y el tabulador sigue desde ahí) y, al volver a la pantalla, a lo que se había tocado. Quien
  // anda por la barra con el teclado o el mouse se queda en la barra.
  const pantallaRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const focoDeAntes = useRef<HTMLElement | null>(null);
  const claveActiva = laActiva?.clave ?? null;
  useLayoutEffect(() => {
    const conFoco = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const enLaBarra = Boolean(conFoco?.closest('[role="tablist"]'));
    if (claveActiva) {
      if (conFoco && pantallaRef.current?.contains(conFoco)) focoDeAntes.current = conFoco;
      if (!enLaBarra) panelRef.current?.focus({ preventScroll: true });
      return;
    }
    if (!enLaBarra) focoDeAntes.current?.focus({ preventScroll: true });
    focoDeAntes.current = null;
  }, [claveActiva]);

  return (
    <>
      <BarraDePestanas pantalla={pantalla} abiertas={abiertas} activa={laActiva ? activa : null} onActivar={activarPestana} onCerrar={cerrarPestana} />
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={pantallaRef}
          id={PANTALLA_DE_FONDO}
          aria-hidden={laActiva ? true : undefined}
          // Las pantallas que se miden contra el alto de la ventana (los tableros) descuentan la barra.
          className={cn('flex min-h-0 flex-1 flex-col', laActiva && 'invisible', abiertas.length > 0 && '[--alto-de-pestanas:3.25rem] md:[--alto-de-pestanas:2.75rem]')}
        >
          {children}
        </div>
        {laActiva && (
          <section
            // Con su clave: al saltar de un asunto a otro, la hoja arranca limpia.
            key={laActiva.clave}
            ref={panelRef}
            role="tabpanel"
            tabIndex={-1}
            id={PANEL_DE_PESTANAS}
            aria-labelledby={idDePestana(laActiva.clave)}
            data-tipo={laActiva.tipo}
            // Los mismos márgenes que la pantalla. Abajo se deja sitio a la barra inferior del
            // teléfono y al botón flotante de Athena: el pie de la hoja (sus botones) no queda
            // debajo de ninguno. En pantallas anchas la hoja va centrada y el botón no la alcanza.
            className="absolute inset-0 flex flex-col px-4 pb-36 pt-4 focus:outline-none md:px-6 md:pb-24 2xl:pb-6"
          >
            <ContenidoDePestana pestana={laActiva} onCerrar={() => cerrarPestana(laActiva.clave)} />
          </section>
        )}
      </div>
    </>
  );
}

function ContenidoDePestana({ pestana, onCerrar }: { pestana: Pestana; onCerrar: () => void }) {
  if (pestana.tipo === 'asunto') return <AsuntoEnPestana asuntoId={pestana.id} onCerrar={onCerrar} />;
  if (pestana.tipo === 'cliente') return <ClienteEnPestana contactId={pestana.id} onCerrar={onCerrar} />;
  if (pestana.tipo === 'chat') return <ChatEnPestana conversationId={pestana.id} mensaje={(pestana.datos as { mensaje?: string | null } | undefined)?.mensaje ?? null} onCerrar={onCerrar} />;
  return <ZoomEnPestana id={pestana.id} datos={pestana.datos} onCerrar={onCerrar} />;
}
